/* import __COLOCATED_TEMPLATE__ from './dac-error.hbs'; */
import Component from '@glimmer/component';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { getTotalErrorInfo } from '../utils/error-utils';

import Utils from '../utils/utils';

/**
 * This is where we need to put the pie chart and the table using inputs
 * This should be dynamic enough to be used by all dac types
 */
export default class DacError extends Component {
  @service intl;
  @service validationService;
  @service('aditools-analytics') analytics;
  @tracked isVolts = false;
  @tracked isCurrentErrorType = false;
  @tracked errorDetails = null;
  @tracked sliceData = null;
  @tracked totalErrorTypeOptions = null;
  @tracked errorHeader = null;
  @tracked totalErrorTypeValue = { id: null, appName: 'ppm' };

  // Total error table
  @tracked totalErrorInfo = null; // all data in multiple unit types
  @tracked totalErrorInfoCurrentUnit = null; // what's displayed in bottom table

  @tracked pieData = null;

  // Total error table pulldown
  defaultTotalErrorTypeOptions = [
    { id: 'ppm', appName: 'ppm' },
    { id: 'fs', appName: 'fs' },
  ];

  calibratedErrorTypeOptions = [{ id: 'ppm', appName: 'ppm' }];

  vErrorTypeOption = { id: 'v', appName: 'v', unit: 'v' };
  currentErrorTypeOption = { id: 'current', appName: 'current', unit: 'a' };

  chart = null;

  constructor(owner, args) {
    super(owner, args);
    this.args.model.chartHandler.current.errorDistribution = this;
    this.errorHeader = this.args.model.chartHandler.data.errorDistribution.errorHeader;
    this.draw();
  }

  willDestroy() {
    super.willDestroy();
    this.args.model.chartHandler.current.errorDistribution = null;
  }

  get errorDetailsExists() {
    return !!this.errorDetails;
  }

  draw() {
    // Don't run error calculations (or display pie chart) if the app is in an error state
    if (this.validationService.errorsExistInModel) return;

    let data = null, // action from parent component
      idealVoltage = null,
      totalErrorTypeOptions = [];

    if (this.args.model.signalChainHandler.dac.data.calibrationToggled) {
      data = this.generateCalibratedDetails();
    } else {
      data = this.generateByPartDetails();
    }

    if (this.args.model.signalChainHandler.dac.data.calibrationToggled) {
      totalErrorTypeOptions.push(...this.calibratedErrorTypeOptions);
    } else {
      // unit type should be ppm, v, or fs
      totalErrorTypeOptions.push(...this.defaultTotalErrorTypeOptions);

      if (data.hasCurrentError) {
        totalErrorTypeOptions.splice(0, 0, this.currentErrorTypeOption);
      }

      if (data.hasIdealVoltage) {
        idealVoltage = data.ideal || this.args.model.fullScaleOutputVoltage; // accept from either
        totalErrorTypeOptions.splice(1, 0, this.vErrorTypeOption);
      }
    }

    this.totalErrorTypeOptions = totalErrorTypeOptions;
    this.totalErrorTypeValue = this.totalErrorTypeOptions[0]; // select first option

    if (this.args.model.selectedTotalErrorType) {
      this.totalErrorTypeValue = this.args.model.selectedTotalErrorType;
    }

    this.totalErrorInfo = getTotalErrorInfo(data, idealVoltage, this.args.model);
    this.handleErrorTypeSelect();

    // re-grab table data
    if (this.errorHeader) {
      this.errorDetails = data.pieData.find((c) => c.name === this.errorHeader)?.details || {}.details;
      this.sliceData = data.pieData.find((c) => c.name === this.errorHeader) || null;
    }

    this.pieData = data.pieData;
  }

  @action updateErrorDetails(sliceData) {
    const currentErrorHeader = this.errorHeader;
    const newErrorHeader = sliceData.name;
    const sliceChanged = currentErrorHeader !== newErrorHeader ? true : false;
    this.sliceData = sliceChanged ? sliceData : null;
    this.errorHeader = sliceChanged ? newErrorHeader : '';
    this.args.model.chartHandler.data.errorDistribution.errorHeader = this.errorHeader;

    if (sliceChanged) {
      this.errorDetails = sliceData.details;
    } else {
      this.errorDetails = '';
    }
    this.analytics.setWebEvent(Utils.getToolName(), 'selectErrorDistributionPieSlice', this.errorHeader);
  }

  @action onErrorTypeSelect(value) {
    this.totalErrorTypeValue = value;
    this.handleErrorTypeSelect();

    this.args.model.selectedTotalErrorType = value;

    this.analytics.setWebEvent(
      Utils.getToolName(),
      'selectErrorDistributionTotalErrorType',
      this.totalErrorTypeValue.appName,
    );
  }

  handleErrorTypeSelect() {
    if (!this.totalErrorInfo) return;
    let errorUnitType = this.totalErrorTypeValue.appName;
    this.totalErrorInfoCurrentUnit = this.totalErrorInfo[errorUnitType];
    this.isVolts = errorUnitType.toLowerCase() === 'v';
    this.isCurrentErrorType = errorUnitType === 'current';
  }

  generateByPartDetails() {
    let errorData = this.args.getErrorData() || {};

    errorData.pieData = (errorData.charts || []).map((errorDataItem) => {
      errorDataItem.details = errorDataItem.details.filter((detailItem) => detailItem.useInChart.byPart);
      return errorDataItem;
    });

    return errorData;
  }

  generateCalibratedDetails() {
    // reorganize data
    let gainDetails = [],
      offsetDetails = [],
      errorData = this.args.getErrorData() || {};

    let generateDetailItem = (detailItem, errorDataItem) => {
      detailItem.header = errorDataItem.name;
      return detailItem;
    };

    (errorData.charts || []).forEach((errorDataItem) => {
      gainDetails.push(
        ...errorDataItem.details
          .filter((detailItem) => detailItem.useInChart.gain)
          .map((detailItem) => generateDetailItem(detailItem, errorDataItem)),
      );
      offsetDetails.push(
        ...errorDataItem.details
          .filter((detailItem) => detailItem.useInChart.offset)
          .map((detailItem) => generateDetailItem(detailItem, errorDataItem)),
      );
    });

    errorData.pieData = [];

    errorData.pieData.push({ name: this.intl.t('labels.gain_errors'), isGain: true, details: gainDetails });
    errorData.pieData.push({ name: this.intl.t('labels.offset_errors'), isOffset: true, details: offsetDetails });

    return errorData;
  }
}
